import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton, QESCheckbox, QESTextInput } from "@app/shared/qes-standards";
import { FC, useContext, useMemo, useState } from "react";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { EditorZip, zipKey } from "./types";
import { ServiceAreaEditorContext } from "./ServiceAreaEditorContext";
import { scrubUserInput } from "@app/shared/utility";

interface ZipFlyoutProps {
  selectedCountySSA: string;
}

/* Some counties don't have zip codes, but it shouldn't be possible to open this flyout
    with a selectedCountySSA of one of those counties
*/
export const ZipFlyout: FC<ZipFlyoutProps> = (props: ZipFlyoutProps) => {
  const editorContext = useContext(ServiceAreaEditorContext);
  const flyoutContext = useContext(FlyoutContext);
  const [flyoutZips, setFlyoutZips] = useState<EditorZip[]>(editorContext.countyToZipLookup[props.selectedCountySSA].map((zipCode) => editorContext.zipLookup[zipKey(props.selectedCountySSA, zipCode)]));
  const [filter, setFilter] = useState("");

  const handleZipCheckboxChange = (zipCode: string) => {
    const newFlyoutZips = structuredClone(flyoutZips);
    // they're all in the selected county, so that ssa is irrelevant here
    const updatingZip = newFlyoutZips.find((countyZip) => countyZip.zipCode === zipCode);
    updatingZip.checkboxState =
      updatingZip.checkboxState === CheckboxStateEnum.Checked ?
        CheckboxStateEnum.Unchecked :
        CheckboxStateEnum.Checked;

    setFlyoutZips(newFlyoutZips);
  }

  const handleSelectAllZips = () => {
    const updatedFlyoutZips = flyoutZips.map((zip) => ({
      ...zip,
      checkboxState: CheckboxStateEnum.Checked,
    }));
    setFlyoutZips(updatedFlyoutZips);
  }

  const handleClearAllZips = () => {
    const updatedFlyoutZips = flyoutZips.map((zip) => ({
      ...zip,
      checkboxState: CheckboxStateEnum.Unchecked,
    }));
    setFlyoutZips(updatedFlyoutZips);
  }

  const handleZipCheckboxChanges = (updatedZips: EditorZip[]) => {
    const updatedZipLookup = structuredClone(editorContext.zipLookup);
    const updatedCountyLookup = structuredClone(editorContext.countyLookup);
    const updatedStateLookup = structuredClone(editorContext.stateLookup);

    updatedZips.forEach((zip) => {
      updatedZipLookup[zipKey(props.selectedCountySSA, zip.zipCode)] = zip;
    });

    const countyZips = editorContext.countyToZipLookup[props.selectedCountySSA];
    const noZipsChecked = countyZips.every((zipCode) => updatedZipLookup[zipKey(props.selectedCountySSA, zipCode)].checkboxState === CheckboxStateEnum.Unchecked);
    const newStatus = noZipsChecked ? RegionSetStatusType.Unknown :
      !!editorContext.countyLookup[props.selectedCountySSA].countyStatus &&
        editorContext.countyLookup[props.selectedCountySSA].countyStatus !== RegionSetStatusType.Unknown ?
        editorContext.countyLookup[props.selectedCountySSA].countyStatus :
        RegionSetStatusType.Active;
    updatedCountyLookup[props.selectedCountySSA].countyStatus = newStatus;

    const stateCounties = editorContext.stateToCountyLookup[editorContext.selectedState];
    const allCountiesIncluded = editorContext.stateToCountyLookup[editorContext.selectedState].every((countySSA) => {
      return updatedCountyLookup[countySSA].countyStatus !== RegionSetStatusType.Unknown &&
        editorContext.countyToZipLookup[countySSA].every((zipCode) => updatedZipLookup[zipKey(countySSA, zipCode)].checkboxState === CheckboxStateEnum.Checked);
    });
    const noCountiesIncluded = stateCounties.every((county) => updatedCountyLookup[county].countyStatus === RegionSetStatusType.Unknown);
    updatedStateLookup[editorContext.selectedState].checkboxState = allCountiesIncluded ?
      CheckboxStateEnum.Checked :
      noCountiesIncluded ?
        CheckboxStateEnum.Unchecked :
        CheckboxStateEnum.Indeterminate;

    editorContext.setStateLookup(updatedStateLookup);
    editorContext.setCountyLookup(updatedCountyLookup);
    editorContext.setZipLookup(updatedZipLookup);
  }

  const closeX = useMemo(() => (
    <span
      className="d-inline-flex align-items-center qes-text-input__clear-button"
      onClick={() => {
        setFilter("");
      }}
    >
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path stroke="#32323c" strokeWidth="5" d="m0 0 100 100m0-100L0 100" /></svg>
    </span>
  ), []);

  const zipsBody: React.ReactNode = useMemo(() => {
    const escapedZipFilter = scrubUserInput(filter);
    // filter the current zips by the regex, case-insensitive
    const zipFilterRegex = new RegExp(`${escapedZipFilter}`, "i");
    const filteredZips = flyoutZips.filter((zip) => !!filter ? zip.zipCode.concat(` (${zip.name})`).match(zipFilterRegex) : true);
    const totalSelected = flyoutZips.filter((zip) => zip.checkboxState === CheckboxStateEnum.Checked).length;

    return (
      <>
        <QESTextInput
          value={filter}
          onChange={(e) => { setFilter(e.target.value) }}
          label="Filter"
          testId="service-area-editor-zip-flyout__filter"
          className="col-3 px-0"
          inputAppend={!!filter && closeX}
        />
        <QESButton
          qesType="secondary"
          qesTestId="service-area-editor-zip-flyout__select-all"
          onClick={handleSelectAllZips}
          className="pl-0"
          disabled={!!filter}
        >
          Select All
        </QESButton>
        <QESButton
          qesType="secondary"
          qesTestId="service-area-editor-zip-flyout__clear-all"
          onClick={handleClearAllZips}
          disabled={!!filter}
        >
          Clear All
        </QESButton>
        <div>Total Selected: {totalSelected} of {flyoutZips.length}</div>
        {filteredZips.map((zip) => (
          <QESCheckbox
            key={zipKey(props.selectedCountySSA, zip.zipCode)}
            checked={zip.checkboxState === CheckboxStateEnum.Checked}
            label={`${zip.zipCode} ${zip.name ? `(${zip.name})` : ""}`}
            testId={`service-area-editor-zip-flyout__zip-checkbox--${zip.zipCode}`}
            onChange={() => { handleZipCheckboxChange(zip.zipCode); }}
          />
        ))}

        <FlyoutFooter
          submitButton={
            <QESButton
              qesType="primary"
              qesTestId="service-area-editor-zip-flyout__save"
              onClick={() => { handleZipCheckboxChanges(flyoutZips); flyoutContext.handleCloseFlyout(); }}
            >
              Save
            </QESButton>
          }
        />
      </>
    );
  }, [flyoutZips, filter]);

  return zipsBody;
}